import { Stack, Group, ActionIcon, Text, Button } from "@mantine/core"
import { MonthPickerInput } from "@mantine/dates"
import { IconArrowLeft, IconArrowRight } from "@tabler/icons-react"
import dayjs from "dayjs"
import { t } from "i18next"
import { Page } from "../../../../../-components/Page"
import { dayJstoDateOnly } from "../../../../../utils/dayjs-to-date-only"
import { ActivityMappingTable } from "./activity-mapping-table"
import { CalculateRoyaltiesModal } from "./calculate-royalties-modal"
import { useDisclosure } from "@mantine/hooks"
import {
  useCalculateRoyaltiesSettlement,
  type RoyaltiesCalculationResponse,
} from "@/orval/gen"
import { showErrorNotification } from "@/utils/form-submission-notifications"

export function RoyaltiesPage() {
  const [selectedMonth, setSelectedMonth] = React.useState(new Date())
  const [calculateModalOpened, calculateModal] = useDisclosure()
  const [calculation, setCalculation] = React.useState<{
    result: RoyaltiesCalculationResponse
    yearMonth: { year: number; month: number }
  } | null>(null)
  const { mutateAsync: calculate, isPending: isCalculating } =
    useCalculateRoyaltiesSettlement()

  const start = React.useMemo(
    () => dayJstoDateOnly(dayjs(selectedMonth).startOf("month")),
    [selectedMonth]
  )
  const end = React.useMemo(
    () => dayJstoDateOnly(dayjs(selectedMonth).endOf("month")),
    [selectedMonth]
  )

  const selectedYearMonth = React.useMemo(
    () => ({
      year: selectedMonth.getFullYear(),
      month: selectedMonth.getMonth() + 1,
    }),
    [selectedMonth]
  )

  const handleCalculate = async () => {
    try {
      const result = await calculate({ data: selectedYearMonth })
      setCalculation({ result, yearMonth: selectedYearMonth })
      calculateModal.open()
    } catch (error) {
      showErrorNotification(
        error instanceof Error ? error : t("CalculationFailed")
      )
    }
  }

  return (
    <Page>
      <Stack h="100%">
        <Group gap="xs">
          <ActionIcon
            variant="subtle"
            color="gray"
            onClick={() => {
              const prev = new Date(selectedMonth)
              prev.setMonth(prev.getMonth() - 1)
              setSelectedMonth(prev)
            }}
          >
            <IconArrowLeft />
          </ActionIcon>
          <MonthPickerInput
            value={selectedMonth}
            onChange={(v) => v && setSelectedMonth(dayjs(v).toDate())}
            w={160}
          />
          <ActionIcon
            variant="subtle"
            color="gray"
            onClick={() => {
              const next = new Date(selectedMonth)
              next.setMonth(next.getMonth() + 1)
              setSelectedMonth(next)
            }}
          >
            <IconArrowRight />
          </ActionIcon>
        </Group>
        <React.Suspense fallback={<Text c="dimmed">{t("Loading")}</Text>}>
          <ActivityMappingTable start={start} end={end} />
        </React.Suspense>
        <Group justify="flex-end" mt="auto">
          <Button
            color="teal"
            onClick={handleCalculate}
            loading={isCalculating}
          >
            {t("CalculateRoyalties")}
          </Button>
        </Group>

        {calculation && (
          <CalculateRoyaltiesModal
            opened={calculateModalOpened}
            onClose={calculateModal.close}
            yearMonth={calculation.yearMonth}
            calculation={calculation.result}
          />
        )}
      </Stack>
    </Page>
  )
}
